import {
  ConversationTypeEnum,
  Environment,
  GenerativeAIConversationItem,
  SystemOfRecord,
  generativeAIConversationItemType
} from "@coxauto-ui/cxm/communications/interfaces";
import { InterstateOnChangeEvent } from "@interstate/components/InterstateEvents";
import { TextArea } from "@interstate/components/TextArea";
import { TextInputEventValue } from "@interstate/components/TextInput";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { GenerativeAIPromptCard } from "./generative-ai-prompt-card";
import { GenerativeAIResponseCard } from "./generative-ai-response-card";
import { Button } from "@interstate/components/Button";
import { ArrowUturnLeftIcon } from "@interstate/components/Icons/ArrowUturnLeftIcon";
import { Alert } from "@interstate/components/Alert";
import { stripHtml } from "@coxauto-ui/cxm/communications/util";

/* eslint-disable-next-line */
export interface GenerativeAIProps {
  customerId: string;
  customerSystem: SystemOfRecord;
  channelType: ConversationTypeEnum;
  env: Environment;
  initialPrompt?: string;
  isSuggestEdit: boolean;
  userDisplayName: string;
  activeListTab: ConversationTypeEnum;
  insertGeneratedResponse: (
    message: string,
    subject?: string,
    shouldOverwriteSubject?: boolean
  ) => void;
  closeGenerativeAI: () => void;
}

const StyledGenerativeAI = styled.div`
  background-color: #f5f6f7;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 8px;

  .generative-ai-header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;

    .generative-ai-header-back-button {
      gap: 8px;
      align-items: baseline;
    }
  }

  .generative-ai-conversation-list {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .generative-ai-hr {
    margin: 0;
  }

  [id^="generative-ai-text-area-"] {
    min-height: 100px;
    max-height: 200px;
  }

  .generative-ai-footer {
    display: flex;
    gap: 16px;
    justify-content: flex-end;
  }
`;

const generateReplyPlaceholder =
  "Add instructions here if desired and click regenerate to generate a new version";
const suggestEditPlaceholder =
  "Add text to be refined or modified for improved clarity and tone";

export function GenerativeAI({
  channelType,
  customerId,
  customerSystem,
  env,
  initialPrompt,
  isSuggestEdit,
  userDisplayName,
  activeListTab,
  insertGeneratedResponse,
  closeGenerativeAI
}: GenerativeAIProps) {
  const [prompt, setPrompt] = useState(stripHtml(initialPrompt || ""));
  const [sessionId, setSessionId] = useState("");
  const [conversationItems, setConversationItems] = useState<
    GenerativeAIConversationItem[]
  >(
    isSuggestEdit
      ? []
      : [
          {
            prompt: "",
            responseMessages: [],
            responseSubject: "",
            type: generativeAIConversationItemType.response
          }
        ]
  );
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (scrollRef.current) {
      setTimeout(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      }, 300);
    }
  }, [conversationItems]);

  // Trigger on initial mount
  useEffect(() => {
    if (isSuggestEdit && conversationItems.length === 0 && initialPrompt) {
      onSuggestEditsClick();
    }
    // We only want this to run once, so we don't want dependencies, but gotta make eslint happy
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (event: InterstateOnChangeEvent<TextInputEventValue>) => {
    setPrompt(`${event.target?.value}` || "");
  };

  const setResponseMessages = (message: string, index: number) => {
    setConversationItems(prev => {
      return prev.map((x, i) => {
        if (i === index) {
          const responseMessages = [...x.responseMessages, message];

          return { ...x, responseMessages };
        }

        return x;
      });
    });
  };

  const setResponseSubject = (
    subject: string,
    index: number,
    append = false
  ) => {
    setConversationItems(prev => {
      return prev.map((x, i) => {
        if (i === index) {
          return {
            ...x,
            responseSubject: append ? x.responseSubject + subject : subject
          };
        }

        return x;
      });
    });
  };

  const onSuggestEditsClick = () => {
    setConversationItems(prev => {
      return [
        {
          prompt,
          responseMessages: [],
          responseSubject: "",
          type: generativeAIConversationItemType.response
        }
      ];
    });
    setPrompt("");
  };

  const onRegenerateClick = () => {
    setConversationItems(prev => {
      return [
        ...prev,
        {
          prompt,
          responseMessages: [],
          responseSubject: "",
          type: generativeAIConversationItemType.prompt
        },
        {
          prompt,
          responseMessages: [],
          responseSubject: "",
          type: generativeAIConversationItemType.response
        }
      ];
    });
    setPrompt("");
  };

  return (
    <StyledGenerativeAI>
      <div
        className="generative-ai-header"
        id={`generative-ai-header-${activeListTab}`}
      >
        <h4 data-testid={`generative-ai-header-title-${activeListTab}`}>
          Generative AI
        </h4>
        <Button
          buttonStyle="aux"
          className="generative-ai-header-back-button"
          data-testid={`generative-ai-header-back-button-${activeListTab}`}
          endIcon={
            <ArrowUturnLeftIcon
              data-testid={`generative-ai-header-close-icon-${activeListTab}`}
            />
          }
          id={`generative-ai-header-back-button-${activeListTab}`}
          onClick={closeGenerativeAI}
        >
          Back to inbox
        </Button>
      </div>
      <div
        className="generative-ai-conversation-list"
        id={`generative-ai-conversation-list-${activeListTab}`}
        ref={scrollRef}
      >
        {conversationItems.map(
          ({ prompt, responseMessages, responseSubject, type }, index) => {
            const key = type + index;
            return type === generativeAIConversationItemType.prompt ? (
              <GenerativeAIPromptCard
                activeListTab={activeListTab}
                index={index}
                key={key}
                prompt={prompt}
                userDisplayName={userDisplayName}
              />
            ) : (
              <GenerativeAIResponseCard
                activeListTab={activeListTab}
                channelType={channelType}
                customerId={customerId}
                customerSystem={customerSystem}
                env={env}
                index={index}
                insertGeneratedResponse={insertGeneratedResponse}
                key={key}
                prompt={prompt}
                responseMessages={responseMessages}
                responseSubject={responseSubject}
                sessionId={sessionId}
                setResponseMessages={setResponseMessages}
                setResponseSubject={setResponseSubject}
                setSessionId={setSessionId}
              />
            );
          }
        )}
      </div>
      <hr
        className="generative-ai-hr"
        data-testid={`generative-ai-hr-${activeListTab}`}
        id={`generative-ai-hr-${activeListTab}`}
      />
      <TextArea
        data-testid={`generative-ai-text-area-${activeListTab}`}
        displayLabel={false}
        id={`generative-ai-text-area-${activeListTab}`}
        label=""
        name={`generative-ai-text-area-${activeListTab}`}
        placeholder={
          isSuggestEdit && conversationItems.length === 0
            ? suggestEditPlaceholder
            : generateReplyPlaceholder
        }
        value={prompt}
        onChange={onChange}
      />
      <Alert
        data-testid={`generative-ai-verify-info-alert-${activeListTab}`}
        id={`generative-ai-verify-info-alert-${activeListTab}`}
        role="alert"
        title="Please Verify Info"
        type="caution"
      >
        <span>
          AI can make mistakes. Check your communication carefully before
          sending
        </span>
      </Alert>
      <div
        className="generative-ai-footer"
        id={`generative-ai-footer-${activeListTab}`}
      >
        <Button
          buttonStyle="secondary"
          data-testid={`generative-ai-cancel-button-${activeListTab}`}
          id={`generative-ai-cancel-button-${activeListTab}`}
          onClick={closeGenerativeAI}
        >
          Cancel
        </Button>
        {isSuggestEdit && conversationItems.length === 0 ? (
          <Button
            buttonStyle="primary"
            data-testid={`generative-ai-suggest-edits-button-${activeListTab}`}
            id={`generative-ai-suggest-edits-button-${activeListTab}`}
            onClick={onSuggestEditsClick}
          >
            Suggest Edits
          </Button>
        ) : (
          <Button
            buttonStyle="primary"
            data-testid={`generative-ai-regenerate-button-${activeListTab}`}
            id={`generative-ai-regenerate-button-${activeListTab}`}
            onClick={onRegenerateClick}
          >
            {"Regenerate"}
          </Button>
        )}
      </div>
    </StyledGenerativeAI>
  );
}

export default GenerativeAI;
